import React, { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { 
  FolderOpen, 
  Clock, 
  CreditCard, 
  FileSignature, 
  FileText, 
  Download 
} from "lucide-react";
import { useToast } from "../../../components/admin/ToastContext";
import { useSelector } from "react-redux";
import { get, download } from "../../../services/lewooApi";
import { PageSkeleton } from "../../../components/admin/LoadingSkeleton";

const CAT_ICONS = { Payslips: CreditCard, Contracts: FileSignature, Certificates: FileText, Documents: FileText };
const MODEL_CAT = { "hr.payslip": "Payslips", "hr.contract": "Contracts", "hr.employee": "Documents" };

const CAT_LABEL_IDS = {
  All: "ess.documents.catAll",
  Payslips: "ess.documents.catPayslips",
  Contracts: "ess.documents.catContracts",
  Certificates: "ess.documents.catCertificates",
  Documents: "ess.documents.catDocuments",
};

function fmtSize(bytes, intl) {
  if (!bytes) return "—";
  if (bytes < 1024) return bytes + " " + intl.formatMessage({ id: "ess.documents.unitB" });
  if (bytes < 1048576) return (bytes / 1024).toFixed(0) + " " + intl.formatMessage({ id: "ess.documents.unitKB" });
  return (bytes / 1048576).toFixed(1) + " " + intl.formatMessage({ id: "ess.documents.unitMB" });
}

const ESSDocumentsPage = () => {
  const intl = useIntl();
  const addToast = useToast();
  const user = useSelector((state) => state.auth.user);
  const [docs, setDocs] = useState([]);
  const [activeCat, setActiveCat] = useState("All");
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const empsRes = await get("/api/employees?limit=500");
        const allEmps = empsRes?.data || [];
        const currentEmp = allEmps.find((e) => e.work_email === user?.email) || null;
        const empId = currentEmp?.id || currentEmp?.employee_id;

        const [attachments, payslipsRes] = await Promise.all([
          empId ? get("/api/documents?employee_id=" + empId + "&limit=100") : { data: [] },
          empId ? get("/api/payslips?employee_id=" + empId + "&limit=50") : { data: [] },
        ]);

        const allAttachments = attachments?.data || [];
        const payslips = payslipsRes?.data || [];
        const slipIds = payslips.map((s) => s.id);

        const mapped = allAttachments
          .filter((a) => {
            if (a.res_model === "hr.payslip") return slipIds.includes(a.res_id);
            return true;
          })
          .map((a) => ({
            name: a.file_name || a.name || intl.formatMessage({ id: "ess.documents.fallbackName" }, { id: a.id }),
            category: MODEL_CAT[a.res_model] || "Documents",
            date: a.created_at ? new Date(String(a.created_at).split("T")[0].split(" ")[0]).toLocaleDateString(intl.locale) : "—",
            size: fmtSize(a.file_size, intl),
            mimetype: a.mime_type || "",
            url: a.id ? `/api/documents/${a.id}/download` : null,
          }));

        setDocs(mapped);
      } catch (_) {
        setDocs([]);
      }
      setLoaded(true);
    };
    load();
  }, [user?.email, intl]);

  const filtered = activeCat === "All" ? docs : docs.filter((d) => d.category === activeCat);
  const categories = ["All", ...new Set(docs.map((d) => d.category))];

  const catLabel = (cat) =>
    CAT_LABEL_IDS[cat]
      ? intl.formatMessage({ id: CAT_LABEL_IDS[cat] })
      : cat;

  const handleDownload = async (row) => {
    try {
      await download(row.url, row.name);
      addToast(intl.formatMessage({ id: "ess.documents.downloading" }, { name: row.name }), "success");
    } catch (_) {
      addToast(intl.formatMessage({ id: "ess.common.submitError" }), "error");
    }
  };

  if (!loaded) return <PageSkeleton />;

  const stats = [
    { labelId: "ess.documents.total", value: String(docs.length), subId: "ess.documents.subTotal", icon: FolderOpen, color: "text-blue-600 bg-blue-100" },
    { labelId: "ess.documents.recent", value: String(docs.filter((d) => d.date?.includes("2026")).length), subId: "ess.documents.subRecent", icon: Clock, color: "text-amber-600 bg-amber-100" },
    { labelId: "ess.documents.payslips", value: String(docs.filter((d) => d.category === "Payslips").length), subId: "ess.documents.subPayslips", icon: CreditCard, color: "text-emerald-600 bg-emerald-100" },
    { labelId: "ess.documents.contracts", value: String(docs.filter((d) => d.category === "Contracts").length), subId: "ess.documents.subContracts", icon: FileSignature, color: "text-violet-600 bg-violet-100" },
  ];

  return (
    <div className="max-w-7xl mx-auto space-y-6 animate-fade-in">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">
          {intl.formatMessage({ id: "ess.documents.title" })}
        </h1>
        <p className="text-gray-500 text-sm mt-1">
          {intl.formatMessage({ id: "ess.documents.subtitle" })}
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {stats.map(({ labelId, value, subId, icon: Icon, color }) => (
          <div key={labelId} className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm transition-all hover:shadow-md">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs font-bold uppercase tracking-wider text-gray-500">{intl.formatMessage({ id: labelId })}</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">{value}</p>
                <p className="text-xs text-gray-400 mt-1">{intl.formatMessage({ id: subId })}</p>
              </div>
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${color}`}>
                <Icon size={20} className={color.split(' ')[0]} />
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="border-b border-gray-100">
          <div className="flex items-center gap-1 px-4 pt-3 overflow-x-auto">
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => setActiveCat(cat)}
                className={`px-3 py-2 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
                  cat === activeCat
                    ? "border-emerald-500 text-emerald-700"
                    : "border-transparent text-gray-500 hover:text-gray-700"
                }`}
              >
                {catLabel(cat)}
                <span className="ml-1.5 text-xs px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-500">
                  {cat === "All" ? docs.length : docs.filter((d) => d.category === cat).length}
                </span>
              </button>
            ))}
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 text-left text-gray-500 border-b border-gray-100">
                <th className="px-5 py-3.5 font-bold uppercase text-[10px] tracking-widest text-slate-400">{intl.formatMessage({ id: "ess.documents.colName" })}</th>
                <th className="px-5 py-3.5 font-bold uppercase text-[10px] tracking-widest text-slate-400">{intl.formatMessage({ id: "ess.documents.colCategory" })}</th>
                <th className="px-5 py-3.5 font-bold uppercase text-[10px] tracking-widest text-slate-400 hidden sm:table-cell">{intl.formatMessage({ id: "ess.documents.colDate" })}</th>
                <th className="px-5 py-3.5 font-bold uppercase text-[10px] tracking-widest text-slate-400 hidden md:table-cell">{intl.formatMessage({ id: "ess.documents.colSize" })}</th>
                <th className="px-5 py-3.5 font-bold uppercase text-[10px] tracking-widest text-slate-400">{intl.formatMessage({ id: "ess.documents.colActions" })}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filtered.length === 0 ? (
                <tr><td colSpan={5} className="px-5 py-12 text-center text-gray-400">{intl.formatMessage({ id: "ess.documents.empty" })}</td></tr>
              ) : (
                filtered.map((row) => {
                  const CategoryIcon = CAT_ICONS[row.category] || FileText;
                  return (
                    <tr key={row.name} className="hover:bg-gray-50/50 transition-colors">
                      <td className="px-5 py-3.5">
                        <div className="flex items-center gap-3">
                          <CategoryIcon size={18} className="text-emerald-500" />
                          <span className="font-semibold text-gray-900">{row.name}</span>
                        </div>
                      </td>
                      <td className="px-5 py-3.5">
                        <span className="inline-flex px-2.5 py-0.5 rounded-full text-xs font-semibold border bg-gray-50 text-gray-600 border-gray-100">{catLabel(row.category)}</span>
                      </td>
                      <td className="px-5 py-3.5 text-gray-500 hidden sm:table-cell">{row.date}</td>
                      <td className="px-5 py-3.5 text-gray-500 hidden md:table-cell">{row.size}</td>
                      <td className="px-5 py-3.5">
                        <div className="flex items-center gap-2">
                          {row.url ? (
                            <button
                              onClick={() => handleDownload(row)}
                              className="text-emerald-600 hover:text-emerald-800 transition-colors"
                              aria-label={intl.formatMessage({ id: "ess.documents.download" })}
                            >
                              <Download size={18} />
                            </button>
                          ) : (
                            <button onClick={() => handleDownload(row)} className="text-emerald-600 hover:text-emerald-800 transition-colors">
                              <Download size={18} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default ESSDocumentsPage;
